import {
  CheckboxField,
  Flex,
  Form,
  Modal,
  SelectField,
  Text,
} from '@/components';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CorporateKindTypeLabelModifier,
  GovernmentSearchSettingFormSchema,
  type GovernmentSearchSettingFormValues,
  corporateKindEnum,
  corporateSearchMatchTypeEnum,
  corporateSearchMatchTypeLabelModifier,
  prefectureOption,
} from '../_models/government-search';
import { PAYMENT_SEARCH_SETTING_DEFAULT_VALUES } from '../_utils/search-government';

const corporateSearchMatchTypeOption = corporateSearchMatchTypeEnum.options.map(
  (value) => ({
    label: corporateSearchMatchTypeLabelModifier[value],
    value,
  }),
);

const corporateKindOption = corporateKindEnum.options.map((value) => ({
  label: CorporateKindTypeLabelModifier[value],
  value,
}));

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSearchSettingChange: (
    searchSetting: GovernmentSearchSettingFormValues,
  ) => void;
  isResetProcessing: boolean;
};

export const SearchSettingModal: FC<Props> = ({
  isOpen,
  onClose,
  onSearchSettingChange,
  isResetProcessing,
}) => {
  const { t } = useTranslation();
  const previousSearchSetting = useRef<GovernmentSearchSettingFormValues>(
    PAYMENT_SEARCH_SETTING_DEFAULT_VALUES,
  );

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    getValues,
  } = useForm<GovernmentSearchSettingFormValues>({
    resolver: zodResolver(GovernmentSearchSettingFormSchema),
    defaultValues: PAYMENT_SEARCH_SETTING_DEFAULT_VALUES,
  });

  const onSubmit = () => {
    previousSearchSetting.current = getValues();
    onSearchSettingChange(getValues());
    onClose();
  };

  const onCancel = useCallback(() => {
    onClose();
    // 値のチラつき防止のためsleep処理を行う
    setTimeout(() => {
      reset(previousSearchSetting.current);
    }, 500);
  }, [onClose, reset]);

  useEffect(() => {
    if (isResetProcessing) {
      reset(PAYMENT_SEARCH_SETTING_DEFAULT_VALUES);
      previousSearchSetting.current = PAYMENT_SEARCH_SETTING_DEFAULT_VALUES;
      onSearchSettingChange(PAYMENT_SEARCH_SETTING_DEFAULT_VALUES);
    }
  }, [isResetProcessing, reset, onSearchSettingChange]);

  const corporateSearchMatchType = useWatch({
    control,
    name: 'matchType',
  });

  return (
    <Modal
      open={isOpen}
      title={t('payment.detail-search')}
      okText={t('payment.search-setting-apply')}
      onOk={onSubmit}
      cancelText={t('cancel')}
      onCancel={onCancel}
      closable={false}
      maskClosable={false}
      width={800}
    >
      <Form leavePageAlertCondition={null} onFinish={handleSubmit(onSubmit)}>
        <Flex vertical gap={4} style={{ marginBottom: 16 }}>
          <SelectField
            name="matchType"
            label={t('payment.corporate-search-match-type')}
            control={control}
            errors={errors}
            options={corporateSearchMatchTypeOption}
            wrapperStyle={{ margin: 0 }}
          />
          {corporateSearchMatchType === 'prefix-match' && (
            <Text
              text={t(
                'payment.corporate-search-match-type.prefix-match-placeholder',
              )}
              color="secondary"
              variant="footnote"
            />
          )}
        </Flex>
        <SelectField
          name="prefecture"
          label={t('payment.prefecture')}
          control={control}
          errors={errors}
          options={prefectureOption}
        />
        <SelectField
          name="corporateKind"
          label={t('payment.corporate-kind')}
          control={control}
          errors={errors}
          options={corporateKindOption}
          mode="multiple"
        />
        <CheckboxField
          name="includeHistorySearch"
          text={t('payment.include-history-search')}
          control={control}
          errors={errors}
        />
        <CheckboxField
          name="includeClosedCorporation"
          text={t('payment.exclude-closed-corporation')}
          control={control}
          errors={errors}
        />
      </Form>
    </Modal>
  );
};
